import "./customerRequest.css";
import "@src/components/compliance/compliance.css";
import "@src/components/uniform/uniform.css";
import { useEffect, useState } from "react";
import { Layout, Table, Tag } from "antd";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Spinner from "@src/components/spinner";
import { FullScreenGrid } from "@src/components/fullScreenGrid/fullScreenGrid";
import { GridSearch, TableActions } from "@src/components/fullScreenGrid/gridToolbar";
import { getDutyRequestsQuery } from "@src/util/query/dutyRequest.query";
import { useServiceRequestActions } from "./serviceRequestActions";

const { Content } = Layout;
const priorityDot = { high: "#FF4D4F", medium: "#FA8C16", low: "#52C41A" };

/**
 * The dashboard's Service Requests tile opens this, full screen: what customers have asked for
 * that's still waiting, with the same actions as the Service Request page - push to the
 * schedule or to mobile services, or reject.
 */
export const ServiceRequestsModal = ({ visible, onClose, onChanged, canModify, liveTick }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const { data, loading, refetch } = useQuery(getDutyRequestsQuery, {
        variables: { input: { page: 1, pageSize: 200, status: "pending" } },
        skip: !visible,
        fetchPolicy: "network-only",
    });
    const actions = useServiceRequestActions({ onChanged: () => { refetch(); onChanged?.(); } });
    // a customer added or withdrew a request, or someone else acted on one
    useEffect(() => { if (visible && liveTick) refetch(); }, [liveTick]);
    const total = data?.getDutyRequests?.response?.totalCount || 0;
    const all = data?.getDutyRequests?.response?.requests || [];
    const requests = all.filter((r) => !search.trim()
        || `${r.siteDetail?.sitename || ""} ${r.customerDetail?.customername || ""}`.toLowerCase().includes(search.trim().toLowerCase()));

    const columns = [
        { title: "Customer", dataIndex: "customer", render: (_, row) => (
            <div className="cmp-person">
                <span className="cmp-person-name">{row.customerDetail?.customername || "—"}</span>
                <span className="cmp-muted">{row.customerDetail?.metadata?.customeremail || ""}</span>
            </div>
        ) },
        { title: "Site", dataIndex: "site", render: (_, row) => (
            <div className="cmp-person">
                <span>{row.siteDetail?.sitename || "—"}</span>
                <span className="cmp-muted">{row.siteDetail?.postcode || ""}</span>
            </div>
        ) },
        { title: "Service Type", dataIndex: "serviceType", render: (v) => (
            <Tag style={{ background: v === "guarding" ? "#e0e7ff" : "#fae8ff", color: v === "guarding" ? "#3730a3" : "#7e22ce", border: "none", borderRadius: 4, fontWeight: 500 }}>
                {v === "guarding" ? "Guarding" : "Mobile Response"}
            </Tag>
        ) },
        { title: "No. of Duties", dataIndex: "numberOfDuties", render: (v, row) => row.serviceType === "guarding"
            ? <span style={{ fontWeight: 600, color: "#33475b" }}>{v ?? "—"}</span>
            : <span style={{ color: "#9ca3af" }}>—</span> },
        { title: "Preferred Date & Time", dataIndex: "preferred", render: (_, row) => {
            const date = row.preferredDate || row.preferredStartTime?.split(" ")?.[0];
            const time = row.preferredStartTime && row.preferredEndTime ? `${row.preferredStartTime} – ${row.preferredEndTime}` : "";
            return <div className="cmp-person"><span>{date || "—"}</span>{time && <span className="cmp-muted">{time}</span>}</div>;
        } },
        { title: "Priority", dataIndex: "priority", render: (v) => v ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, textTransform: "capitalize" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: priorityDot[v] || "#ccc" }} />{v}
            </span>
        ) : "—" },
        { title: "Submitted", dataIndex: "createdAt", render: (v) => (
            <div className="cmp-person"><span>{v ? dayjs(v).format("DD/MM/YYYY") : "—"}</span><span className="cmp-muted">{v ? dayjs(v).format("HH:mm") : ""}</span></div>
        ) },
        canModify && { title: "", dataIndex: "actions", align: "right", render: (_, row) => (
            <div className="uni-actions">
                {row.serviceType === "mobile"
                    ? <button className="drawer-filled-btn" onClick={() => actions.pushToMobile(row)}>Push to Mobile Services</button>
                    : <button className="drawer-filled-btn" disabled={actions.siteLoading} onClick={() => actions.pushToSchedule(row)}>{actions.siteLoading ? <Spinner /> : "Push to Schedule"}</button>}
                <button className="sm-btn" disabled={actions.actionLoading} onClick={() => actions.reject(row)}>Reject</button>
            </div>
        ) },
    ].filter(Boolean);

    return (
        <FullScreenGrid
            visible={visible}
            onClose={onClose}
            title="Service Requests"
            subtitle={`${total} pending`}
            intro="Duty requests your customers have submitted that are waiting - push them to the schedule or mobile services, or reject them."
            stats={[
                { label: "Pending", value: total },
                { label: "Guarding", value: all.filter((r) => r.serviceType === "guarding").length },
                { label: "Mobile Response", value: all.filter((r) => r.serviceType !== "guarding").length },
                { label: "High priority", value: all.filter((r) => r.priority === "high").length },
            ]}
        >
            <div className="fsg-toolbar" style={{ marginTop: 20 }}>
                <GridSearch value={search} onChange={setSearch} placeholder="Search by Customer or Site..." />
                <div className="fsg-toolbar-actions">
                    <TableActions actions={[{ key: "all", label: "Open all service requests", onClick: () => { onClose(); navigate("/user/servicerequest"); } }]} />
                </div>
            </div>
            <div className="tableView customerRequest">
                <Layout className="bg-white all-module-grid">
                    <Content className="site-layout">
                        <Table
                            className="curvedTable"
                            loading={loading}
                            columns={columns}
                            dataSource={requests.map((row) => ({ ...row, key: row._id }))}
                            scroll={{ x: "max-content" }}
                            pagination={{ pageSize: 20, hideOnSinglePage: true }}
                            locale={{ emptyText: "No service requests waiting" }}
                        />
                    </Content>
                </Layout>
            </div>
            {actions.modals}
        </FullScreenGrid>
    );
};
